/**
 * What the page in front of the person shows, as data, for OS
 * (docs/plans/os-live-mode.md, Decided 10).
 *
 * A record's screen describes itself here (`useReportView`) while it is up,
 * and lets go when it goes. A chat turn reads it when it is sent, and a live
 * call sends it up as `place` whenever it changes (the phone's
 * `use-assistant-live.ts`, the web's `use-live-place.ts`). One view at a
 * time: the page showing. It belongs to a record, so a turn about another
 * record (or none) never carries it (`viewFor`).
 */
import type { AgentView } from '@alluzo/shared';
import { useEffect } from 'react';
import { create } from 'zustand';

type HeldView = {
  /** The record the page belongs to. */
  readonly recordId: string;
  readonly view: AgentView;
  /** The view as text, so a render that drew the same list again changes nothing. */
  readonly key: string;
};

type AgentViewState = {
  held: HeldView | null;
  report: (recordId: string, view: AgentView) => void;
  /** The page went: only its own view is let go, never one a newer page reported. */
  clear: (recordId: string, key: string) => void;
};

export const useAgentViewStore = create<AgentViewState>()((set, get) => ({
  held: null,
  report: (recordId, view) => {
    const key = JSON.stringify(view);
    const held = get().held;
    if (held !== null && held.recordId === recordId && held.key === key) return;
    set({ held: { recordId, view, key } });
  },
  clear: (recordId, key) => {
    const held = get().held;
    if (held !== null && held.recordId === recordId && held.key === key) set({ held: null });
  },
}));

/** The view showing, when it is the page of this record; undefined anywhere else. */
export const viewFor = (recordId: string | null | undefined): AgentView | undefined => {
  const held = useAgentViewStore.getState().held;
  return held !== null && recordId !== null && recordId !== undefined && held.recordId === recordId ? held.view : undefined;
};

/** `{ view }` for a context, or nothing: spread into a chat or live context. */
export const viewWith = (recordId: string | null | undefined): { view?: AgentView } => {
  const view = viewFor(recordId);
  return view === undefined ? {} : { view };
};

/**
 * A page says what it shows, for as long as it shows it. `view` undefined (a
 * list still loading) lets go of what this page said before.
 */
export const useReportView = (recordId: string, view: AgentView | undefined): void => {
  // The view as text stands for it: a new object with the same contents is the same view.
  const key = view === undefined ? null : JSON.stringify(view);
  useEffect(() => {
    if (key === null) return undefined;
    useAgentViewStore.getState().report(recordId, JSON.parse(key) as AgentView);
    return () => useAgentViewStore.getState().clear(recordId, key);
  }, [recordId, key]);
};
